import { Check, Copy, ShieldCheck } from 'lucide-react';
import { useEffect, useState } from 'react';
import QRCode from 'qrcode';
import { api } from '../lib/api';
import type { Parent } from '../lib/types';
import { Fold } from '../ui/Collapsible';
import { Logo } from '../ui/Logo';
import { useParent } from './ParentContext';
import { browserSupportsWebAuthn } from '@simplewebauthn/browser';
import { addPasskey } from './Passkeys';

/** Recovery codes shown once, with copy/download. */
function RecoveryCodes({ codes, onDone }: { codes: string[]; onDone: () => void }) {
  const { toast } = useParent();
  const text = codes.join('\n');
  function download() {
    const url = URL.createObjectURL(new Blob([`ClassGuardian recovery codes\n\n${text}\n`], { type: 'text/plain' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = 'classguardian-recovery-codes.txt';
    a.click();
    URL.revokeObjectURL(url);
  }
  return (
    <div className="stack">
      <p className="good-text">
        <ShieldCheck size={16} /> Two-factor is on.
      </p>
      <p>
        Save these <strong>recovery codes</strong> somewhere safe. Each works once if you lose your phone. This is the only time
        they’re shown.
      </p>
      <ul className="recovery-codes">
        {codes.map((c) => (
          <li key={c}>{c}</li>
        ))}
      </ul>
      <div className="actions">
        <button className="btn small" onClick={() => navigator.clipboard?.writeText(text).then(() => toast({ text: 'Copied.', tone: 'good' }))}>
          <Copy size={15} /> Copy
        </button>
        <button className="btn small" onClick={download}>
          Download
        </button>
        <button className="btn small primary" onClick={onDone}>
          I’ve saved them
        </button>
      </div>
    </div>
  );
}

/** The scan-and-confirm flow to turn two-factor on. Calls onEnabled with the fresh account. */
function TwoFactorSetup({ onEnabled }: { onEnabled: (p: Parent) => void }) {
  const { parent, setParent, toast } = useParent();
  const [secret, setSecret] = useState('');
  const [qr, setQr] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [recovery, setRecovery] = useState<string[] | null>(null);
  const [showSecret, setShowSecret] = useState(false);

  useEffect(() => {
    api
      .post<{ secret: string; otpauthUrl: string }>('/api/me/mfa/setup')
      .then((r) => {
        setSecret(r.secret);
        QRCode.toDataURL(r.otpauthUrl, { margin: 1, width: 220 }).then(setQr).catch(() => setQr(''));
      })
      .catch((e) => toast({ text: (e as Error).message, tone: 'alert' }));
  }, [toast]);

  async function enable() {
    setBusy(true);
    try {
      const r = await api.post<{ recoveryCodes: string[] }>('/api/me/mfa/enable', { secret, code: code.replace(/\D/g, '') });
      setRecovery(r.recoveryCodes);
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
    } finally {
      setBusy(false);
    }
  }

  if (recovery) {
    return (
      <RecoveryCodes
        codes={recovery}
        onDone={() => {
          const next = { ...parent, mfaEnabled: true };
          setParent(next);
          onEnabled(next);
        }}
      />
    );
  }

  return (
    <div className="stack">
      <p>
        Scan this with an authenticator app (Google Authenticator, Authy, 1Password…), then enter the 6-digit code it shows.
      </p>
      {qr ? <img className="qr" src={qr} alt="Authenticator QR code" width={200} height={200} /> : <p className="muted">Preparing…</p>}
      <p className="muted small">
        Can’t scan?{' '}
        <button type="button" className="link-btn" onClick={() => setShowSecret((s) => !s)}>
          Enter a code by hand
        </button>
        {showSecret && (
          <>
            {' '}
            <code className="mono">{secret}</code>
          </>
        )}
      </p>
      <label>
        6-digit code
        <input
          inputMode="numeric"
          autoComplete="one-time-code"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          maxLength={6}
          placeholder="123456"
        />
      </label>
      <div>
        <button className="btn primary" onClick={enable} disabled={busy || code.replace(/\D/g, '').length !== 6 || !secret}>
          <Check size={16} /> {busy ? 'Checking…' : 'Turn on two-factor'}
        </button>
      </div>
    </div>
  );
}

/** Full-page, mandatory 2FA setup shown when the server requires it and the account hasn't enrolled. */
export function MfaGate({ onDone }: { onDone: (p: Parent) => void }) {
  const { signOut } = useParent();
  return (
    <main className="auth">
      <div className="auth-card card">
        <Logo large />
        <h1>Set up two-factor</h1>
        <p className="muted">This ClassGuardian server requires two-factor sign-in. Set it up now to continue.</p>
        <TwoFactorSetup onEnabled={onDone} />
        <PasskeyInstead onDone={onDone} />
        <p className="muted center">
          <button type="button" className="link-btn" onClick={signOut}>
            Sign out
          </button>
        </p>
      </div>
    </main>
  );
}

/** The Settings card: turn 2FA on, regenerate recovery codes, or turn it off. */
export function MfaSettings() {
  const { parent, setParent, toast } = useParent();
  const [setting, setSetting] = useState(false);
  const [password, setPassword] = useState('');
  const [mode, setMode] = useState<'idle' | 'disable' | 'recovery' | 'change'>('idle');
  const [recovery, setRecovery] = useState<string[] | null>(null);
  const [changing, setChanging] = useState(false);
  const [busy, setBusy] = useState(false);

  async function act(path: string, done: string) {
    setBusy(true);
    try {
      const r = await api.post<{ recoveryCodes?: string[] }>(path, { password });
      setPassword('');
      setMode('idle');
      if (r.recoveryCodes) setRecovery(r.recoveryCodes);
      else if (path.endsWith('/reset')) {
        // Cleared the old authenticator; set a new one up right away.
        setParent({ ...parent, mfaEnabled: false });
        setChanging(true);
      } else {
        setParent({ ...parent, mfaEnabled: false });
        toast({ text: done, tone: 'good' });
      }
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
    } finally {
      setBusy(false);
    }
  }

  // A fold in Settings → Account security (#415, #601); a link to #twofactor opens it.
  const summary = changing ? 'Changing authenticator' : parent.mfaEnabled ? `On · authenticator app${parent.mfaRequired ? ' · required' : ''}` : 'Off';
  return (
    <Fold id="twofactor" title="Two-factor authentication" summary={summary}>
      {changing ? (
        <>
          <p className="muted small">Set up your new authenticator.</p>
          <TwoFactorSetup onEnabled={() => { setChanging(false); setParent({ ...parent, mfaEnabled: true }); }} />
        </>
      ) : recovery ? (
        <RecoveryCodes codes={recovery} onDone={() => setRecovery(null)} />
      ) : parent.mfaEnabled ? (
        <>
          <p className="good-text">
            <ShieldCheck size={16} /> On. You’ll enter a code from your authenticator app when you sign in.
          </p>
          {mode === 'idle' ? (
            <div className="actions">
              <button className="btn small" onClick={() => setMode('recovery')}>
                New recovery codes
              </button>
              <button className="btn small" onClick={() => setMode('change')}>
                New phone? Change authenticator
              </button>
              {!parent.mfaRequired && (
                <button className="btn small danger-text" onClick={() => setMode('disable')}>
                  Turn off
                </button>
              )}
              {parent.mfaRequired && <span className="muted small">Two-factor is required, so it can’t be turned off.</span>}
            </div>
          ) : (
            <div className="stack">
              <label>
                Confirm your password
                <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" />
              </label>
              <div className="actions">
                <button
                  className={`btn small ${mode === 'disable' ? 'danger-text' : 'primary'}`}
                  disabled={busy || !password}
                  onClick={() =>
                    mode === 'disable'
                      ? act('/api/me/mfa/disable', 'Two-factor is off.')
                      : mode === 'change'
                        ? act('/api/me/mfa/reset', '')
                        : act('/api/me/mfa/recovery', '')
                  }
                >
                  {mode === 'disable' ? 'Turn off two-factor' : mode === 'change' ? 'Continue' : 'Generate new codes'}
                </button>
                <button className="btn small" onClick={() => { setMode('idle'); setPassword(''); }} disabled={busy}>
                  Cancel
                </button>
              </div>
            </div>
          )}
        </>
      ) : setting ? (
        <TwoFactorSetup onEnabled={() => setSetting(false)} />
      ) : (
        <>
          <p className="muted small">Add a second step at sign-in using an authenticator app on your phone. Strongly recommended.</p>
          <div>
            <button className="btn primary" onClick={() => setSetting(true)}>
              Set up two-factor
            </button>
          </div>
        </>
      )}
    </Fold>
  );
}

/** On the set-up screen: a passkey can be the second factor instead, where the admin allows it (#417). */
function PasskeyInstead({ onDone }: { onDone: (p: Parent) => void }) {
  const { parent, toast } = useParent();
  const [busy, setBusy] = useState(false);
  if (!parent.passkeys?.enabled || !browserSupportsWebAuthn()) return null;
  async function add() {
    setBusy(true);
    try {
      if (!(await addPasskey('Passkey'))) return;
      const me = await api.get<Parent>('/api/me');
      if (me.mfaSatisfied) onDone(me);
      else toast({ text: 'Passkey added. This server still needs an authenticator app as well.', tone: 'info' });
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="stack center">
      <p className="muted small">Or use a passkey: your face, fingerprint or screen lock.</p>
      <button type="button" className="btn" onClick={() => void add()} disabled={busy}>
        {busy ? 'Follow your device’s prompt…' : 'Add a passkey instead'}
      </button>
    </div>
  );
}
